import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from '@klear/ui/lib/trpc'
import { trpc } from '@klear/ui/lib/trpc'
import { useMutationWithToast, createToastConfig } from '@klear/ui/hooks/use-mutations-with-toast'

type SupplierList = RouterOutputs['supplier']['list']
export type SupplierRow = SupplierList[number]
type SupplierMutationResult = RouterOutputs['supplier']['create']
type SupplierCreateInput = RouterInputs['supplier']['create']
type SupplierUpdateInput = RouterInputs['supplier']['update']
type SupplierPreviewInput = RouterInputs['supplier']['preview']
type SupplierPreviewResult = RouterOutputs['supplier']['preview']

export function useSuppliers(traderId?: number): {
  suppliers: SupplierList
  error: ReturnType<typeof trpc.supplier.list.useQuery>['error']
  isLoading: boolean
} {
  const { data, error, isLoading } = trpc.supplier.list.useQuery({ traderId })

  return { suppliers: data ?? [], error, isLoading }
}

export function useSupplier(id: number, traderId?: number) {
  const {
    data: supplier,
    error,
    isLoading
  } = trpc.supplier.get.useQuery(
    { id, traderId },
    {
      enabled: !!id
    }
  )

  return { supplier, error, isLoading }
}

const invalidateQueries = ['supplier.list', 'supplier.get']

export function useSupplierMutations() {
  const supplierName = (data: SupplierMutationResult) => data.name ?? ''
  const utils = trpc.useUtils()
  const [isPreviewing, setIsPreviewing] = useState(false)

  const createMutation = useMutationWithToast(
    trpc.supplier.create.useMutation,
    createToastConfig<SupplierMutationResult>('Supplier', 'create', supplierName),
    { invalidateQueries }
  )
  const updateMutation = useMutationWithToast(
    trpc.supplier.update.useMutation,
    createToastConfig<SupplierMutationResult>('Supplier', 'update', supplierName),
    { invalidateQueries }
  )

  const deleteSupplierMutation = useMutationWithToast(
    trpc.supplier.delete.useMutation,
    createToastConfig('Supplier', 'delete'),
    {
      invalidateQueries
    }
  )

  const previewSupplierCall = async (input: SupplierPreviewInput): Promise<SupplierPreviewResult> => {
    setIsPreviewing(true)
    try {
      return await utils.supplier.preview.fetch(input)
    } finally {
      setIsPreviewing(false)
    }
  }

  const previewNewSupplier = (
    input: Extract<SupplierPreviewInput, { id?: undefined }>
  ): Promise<SupplierPreviewResult> => previewSupplierCall(input)

  const previewSupplier = (input: Extract<SupplierPreviewInput, { id: number }>): Promise<SupplierPreviewResult> =>
    previewSupplierCall(input)

  const commitNewSupplier = (input: SupplierCreateInput): Promise<SupplierMutationResult> =>
    createMutation.mutateAsync(input)

  const commitSupplier = (input: SupplierUpdateInput): Promise<SupplierMutationResult> =>
    updateMutation.mutateAsync(input)

  const deleteSupplier = (input: { id: number; traderId: number }) =>
    deleteSupplierMutation.mutateAsync({ id: input.id, traderId: input.traderId })

  return {
    previewNewSupplier,
    previewSupplier,
    commitNewSupplier,
    commitSupplier,
    deleteSupplier,
    isPreviewing,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteSupplierMutation.isPending
  }
}
